import React, { useState, useEffect, useRef } from 'react';

/**
 * ColumnFilter - Simple reusable column filter dropdown component
 * 
 * This is a simplified version for basic filter needs (LabourRatesLog, RentalsLog).
 * For advanced filtering features, see Matrix/cells/ColumnFilter.js
 * 
 * Props:
 * - values: Array of unique values available for filtering
 * - selectedValues: Array of currently selected filter values
 * - onChange: Callback when selection changes (receives array of selected values)
 * - theme: 'light' | 'dark' (default: 'dark')
 * - accentColor: Active filter indicator color (default: '#17a2b8')
 */
const ColumnFilter = ({ 
  values, 
  selectedValues = [], 
  onChange,
  theme = 'dark',
  accentColor = '#17a2b8'
}) => {
  const [filterOpen, setFilterOpen] = useState(false);
  const ref = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        setFilterOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const toggleValue = (val) => {
    const newSelected = selectedValues.includes(val) 
      ? selectedValues.filter(v => v !== val) 
      : [...selectedValues, val];
    onChange(newSelected);
  };

  const isDark = theme === 'dark';
  const hasActiveFilter = selectedValues.length > 0;

  const styles = {
    container: {
      position: 'relative',
      display: 'inline-block',
    },
    button: {
      background: hasActiveFilter ? accentColor : 'transparent',
      border: 'none',
      color: isDark ? '#fff' : '#333',
      cursor: 'pointer',
      padding: '2px 4px',
      borderRadius: '3px',
      fontSize: '10px',
    },
    dropdown: {
      position: 'absolute',
      top: '100%',
      left: 0,
      backgroundColor: isDark ? '#2a2a3a' : '#fff',
      border: `1px solid ${isDark ? '#4a4a5a' : '#ddd'}`,
      borderRadius: '4px',
      zIndex: 100,
      minWidth: '150px',
      maxHeight: '200px',
      overflow: 'auto',
      padding: '8px',
      boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
    },
    clearButton: {
      width: '100%',
      padding: '4px',
      marginBottom: '4px',
      backgroundColor: '#6c757d',
      color: '#fff',
      border: 'none',
      borderRadius: '3px',
      cursor: 'pointer',
      fontSize: '11px',
    },
    label: {
      display: 'flex',
      alignItems: 'center',
      gap: '6px',
      padding: '4px',
      cursor: 'pointer',
      fontSize: '12px',
      color: isDark ? '#e0e0e0' : '#333',
    },
  };

  return (
    <div ref={ref} style={styles.container}>
      <button 
        onClick={(e) => { e.stopPropagation(); setFilterOpen(!filterOpen); }}
        style={styles.button}
      >
        ▼
      </button>
      {filterOpen && (
        <div style={styles.dropdown}>
          <button 
            onClick={() => onChange([])} 
            style={styles.clearButton}
          >
            Clear All
          </button>
          {values.map(val => (
            <label key={val} style={styles.label}>
              <input 
                type="checkbox" 
                checked={selectedValues.includes(val)} 
                onChange={() => toggleValue(val)} 
              />
              {val || '(empty)'}
            </label>
          ))}
        </div>
      )}
    </div>
  );
};

export default ColumnFilter;

